import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { MathUtils } from 'three';
import { DetailSectionIDs, SectionData, SectionIDs, Sections } from '../data/Sections';
import { StateSetter } from '../types';
import { useDetailViewContext } from './DetailViewContext';

export type NavigationData = {
  scrollValue: number;
  setScrollValue: StateSetter<number>,
  isMenuOpen: boolean;
  setIsMenuOpen: StateSetter<boolean>,
  scrollEnabledUp: boolean;
  setScrollEnabledUp: StateSetter<boolean>;
  scrollEnabledDown: boolean;
  setScrollEnabledDown: StateSetter<boolean>;
  getScrollValueForSection: (id: string) => number;
  sections: SectionData[];
  activeSectionID: SectionIDs;
  moveToTheSection: (sectionId: string) => void;
  scrollUpdate: number;
};

export const NavigationContext = createContext<NavigationData>({
  scrollValue: 0,
  getScrollValueForSection: () => 0,
  setScrollValue: () => ({}),
  scrollEnabledUp: true,
  setScrollEnabledUp: () => ({}),
  scrollEnabledDown: true,
  setScrollEnabledDown: () => ({}),
  isMenuOpen: false,
  setIsMenuOpen: () => ({}),
  sections: [],
  activeSectionID: SectionIDs.Title,
  moveToTheSection: () => console.log('empty method'),
  scrollUpdate: 0
  // isSetSectionsActivated: false
});

export default function NavigationContextProvider({ children }: JSX.ElementChildrenAttribute): JSX.Element
{
  const {setDetailSectionID} = useDetailViewContext();

  // a value between 0 and 1
  const [ scrollValue, setScrollValue ] = useState(0);
  const [ activeSectionID, setActiveSectionID ] = useState<SectionIDs>(SectionIDs.Title);
  const [ scrollEnabledUp, setScrollEnabledUp ] = useState(true);
  const [ scrollEnabledDown, setScrollEnabledDown ] = useState(true);
  const [ scrollUpdate, setScrollUpdate ] = useState(0);
  const [ isMenuOpen, setIsMenuOpen ] = useState(false);

  // make sure these exist at the start, because they'll be used by the rest of the app immediately
  const sections: SectionData[] = useMemo(() =>
  {
    const sections = Sections.map(def =>
    {
      return {
        active: false,
        ratio: 0,
        start: 0,
        end: 0,
        ...def
      };
    });

    let lastPosition = 0;
    const totalDuration = sections.reduce((a, b) => a + b.relativeDuration + b.startDelay, 0);
    const durationRatio = 1 / totalDuration;

    sections.forEach(section =>
    {
      section.start = lastPosition + section.startDelay * durationRatio;
      section.end = lastPosition + (section.relativeDuration + section.startDelay) * durationRatio;
      lastPosition = section.end;
    });

    return sections;
  }
  , []);

  const forceScrollValue = useCallback((value: number) =>
  {
    setScrollValue(value);
    setScrollUpdate(value => value + 1);
  }, []);

  const moveToTheSection = useCallback((sectionId: string) =>
  {
    const section = sections.find(s => s.id === sectionId);
    if (!section)
      return;
    setDetailSectionID(DetailSectionIDs.None);
    setScrollEnabledUp(false);
    setScrollEnabledDown(false);
    forceScrollValue(section.start + section.snapPoints[0] * (section.end - section.start));
    setScrollEnabledUp(true);
    setScrollEnabledDown(true);
  }, [forceScrollValue, setDetailSectionID, sections]);

  useEffect(() =>
  {
    for (let i = 0; i < sections.length; ++i) {
      const section = sections[i];

      if (scrollValue >= section.start && scrollValue < section.end) {
        const pageRatio = MathUtils.mapLinear(scrollValue, section.start, section.end, 0, 1);

        section.active = true;
        section.ratio = pageRatio;
      }
      else {
        section.active = false;
      }
    }
  }, [ scrollValue, sections ]);

  useEffect(() =>
  {
    for (const section of sections) {
      if (scrollValue >= section.start && scrollValue < section.end) {
        setActiveSectionID(section.id as SectionIDs);
        return;
      }
    }
  }, [ scrollValue, sections ]);

  const getScrollValueForSection = useCallback((id: string) =>
  {
    const section = sections.find(s => s.id === id);
    if (!section)
      return 0;
    // when deep-linking, we want to go straight to where the previous page has gone, so skip beyond "last ratio"
    return section.start + (section.end - section.start) * section.snapPoints[0];
  }, [ sections ]);

  return (
    <NavigationContext.Provider
      value={{
        scrollValue,
        setScrollValue,
        scrollEnabledUp,
        setScrollEnabledUp,
        scrollEnabledDown,
        setScrollEnabledDown,
        getScrollValueForSection,
        sections,
        activeSectionID,
        moveToTheSection,
        isMenuOpen,
        setIsMenuOpen,
        // this is used by the scroll controller to know to update its target scroll position
        scrollUpdate
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
}

export const useNavigationContext: () => NavigationData = () => useContext(NavigationContext);
